import React, { useCallback, useEffect, useState } from "react";
import { adminAuthAPI, adminUsersAPI } from "../services/api";
import { roleLabel } from "../utils/roleAccess";
import RolePermissionsInfo from "../components/RolePermissionsInfo";
import RulesPermissions from "./admin/RulesPermissions";
import RulesAuditLog from "./admin/RulesAuditLog";
import { useRules } from "../hooks/useRules";
import "./admin/RulesPermissions.css";

// Wizetok Administration: manage who in this tenant can use Wizetok and with
// which roles. Works on its own — no ReachOut needed. A user may hold several
// roles; their capabilities add up. Admins pick any roles, managers add
// recruiters, recruiters add viewers (same rules as ReachOut).

const C = {
  brand: "#6366F1",
  border: "#E5E7EB",
  muted: "#6B7280",
  danger: "#DC2626",
};

const ALL_ROLES = ["admin", "hr_manager", "recruiter", "viewer"];

const chip = (role) => ({
  display: "inline-block",
  fontSize: 11,
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: 999,
  marginRight: 4,
  background: role === "admin" ? "#1B3A4B" : role === "hr_manager" ? "#F5C518" : role === "recruiter" ? "#22A06B" : "#9CA3AF",
  color: role === "hr_manager" ? "#3D2E00" : "#fff",
});

function RolePicker({ options, value, onChange, disabled, locked = [] }) {
  const toggle = (role) => {
    if (locked.includes(role)) return;
    onChange(value.includes(role) ? value.filter((r) => r !== role) : [...value, role]);
  };
  return (
    <span style={{ display: "inline-flex", gap: 12, flexWrap: "wrap" }}>
      {options.map((role) => (
        <label key={role} style={{ display: "inline-flex", gap: 4, alignItems: "center", fontSize: 13 }}>
          <input type="checkbox" checked={value.includes(role)} disabled={disabled || locked.includes(role)} onChange={() => toggle(role)} />
          {roleLabel(role)}
        </label>
      ))}
    </span>
  );
}

function errorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

function UsersTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [addRoles, setAddRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editRoles, setEditRoles] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await adminUsersAPI.list();
      setData(res.data);
      setError("");
      const grantable = res.data?.grantableRoles || [];
      setAddRoles((prev) => (prev.length ? prev : grantable.slice(-1)));
    } catch (err) {
      setError(errorText(err, "Could not load users."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grantable = data?.grantableRoles || [];
  const isAdmin = data?.callerRole === "admin";
  const pickable = isAdmin ? ALL_ROLES : grantable;
  const usePicker = pickable.length > 1;
  const canChangeRoles = data?.canChangeRoles !== false;
  const canRemove = data?.canRemove !== false;

  const run = async (fn, ok) => {
    setBusy(true);
    setNotice("");
    setError("");
    try {
      await fn();
      setNotice(ok);
      await load();
    } catch (err) {
      setError(errorText(err, "That change could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  const onAdd = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    const roles = usePicker ? addRoles.filter((r) => pickable.includes(r)) : grantable;
    if (!roles.length) {
      setError("Pick at least one role.");
      return;
    }
    run(async () => {
      await adminUsersAPI.add(email.trim(), roles, name.trim() || undefined);
      setEmail("");
      setName("");
    }, "User added.");
  };

  const onSaveRoles = (row) => {
    if (!editRoles.length) {
      setError("Pick at least one role.");
      return;
    }
    run(async () => {
      await adminUsersAPI.updateRoles(row.email, editRoles);
      setEditing(null);
      if (row.isSelf) {
        // Own roles changed: refresh the stored profile so menus follow at once.
        const me = await adminAuthAPI.getCurrentAdmin();
        localStorage.setItem("admin", JSON.stringify(me.data));
        window.dispatchEvent(new Event("storage"));
      }
    }, "Roles updated.");
  };

  const onRemove = (row) => {
    if (!window.confirm(`Remove ${row.email}'s Wizetok access? Their ReachOut access is not affected.`)) return;
    run(() => adminUsersAPI.remove(row.email), "Wizetok access removed.");
  };

  if (!data && !error) return <div style={{ padding: 24 }}>Loading…</div>;

  const users = data?.users || [];

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 0 40px" }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
        Administration <RolePermissionsInfo />
      </h1>
      <p style={{ fontSize: 13, color: C.muted, margin: "4px 0 20px" }}>
        {isAdmin
          ? "Everyone in your company who can use Wizetok. A user can hold several roles."
          : "You and the users you added."}
      </p>

      {error && <div role="alert" style={{ color: C.danger, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {notice && <div style={{ color: "#15803D", fontSize: 13, marginBottom: 12 }}>{notice}</div>}

      {data && grantable.length > 0 && (
        <form
          onSubmit={onAdd}
          style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", padding: 14,
                   border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 18, background: "#fff" }}
        >
          <input
            type="email" required placeholder="User email *" value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ flex: "2 1 220px", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: 8 }}
          />
          <input
            placeholder="Name (optional)" value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ flex: "1 1 160px", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: 8 }}
          />
          {usePicker ? (
            <RolePicker options={pickable} value={addRoles} onChange={setAddRoles} disabled={busy} />
          ) : (
            <span style={{ fontSize: 13, color: C.muted }}>
              Adds as {grantable.map(roleLabel).join(", ")}
            </span>
          )}
          <button
            type="submit" disabled={busy}
            style={{ padding: "8px 16px", border: "none", borderRadius: 8, background: C.brand, color: "#fff", fontWeight: 600 }}
          >
            {busy ? "Saving…" : "Add user"}
          </button>
        </form>
      )}

      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
              <th style={{ padding: "10px 12px" }}>Name</th>
              <th style={{ padding: "10px 12px" }}>Email</th>
              <th style={{ padding: "10px 12px" }}>Roles</th>
              <th style={{ padding: "10px 12px" }}>Added by</th>
              <th style={{ padding: "10px 12px", width: 150 }} />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 16, color: C.muted }}>No users yet.</td></tr>
            )}
            {users.map((row) => (
              <tr key={row.email} style={{ borderBottom: "1px solid #F3F4F6" }}>
                <td style={{ padding: "10px 12px" }}>{row.name}{row.isSelf ? " (you)" : ""}</td>
                <td style={{ padding: "10px 12px", color: C.muted }}>{row.email}</td>
                <td style={{ padding: "10px 12px" }}>
                  {editing === row.email ? (
                    <RolePicker options={pickable} value={editRoles} onChange={setEditRoles} disabled={busy}
                      locked={row.isSelf ? ["admin"] : []} />
                  ) : (
                    row.roles.map((r) => <span key={r} style={chip(r)}>{roleLabel(r)}</span>)
                  )}
                </td>
                <td style={{ padding: "10px 12px", color: C.muted }}>{row.invitedByEmail || "—"}</td>
                <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                  {row.canEdit && editing === row.email && (
                    <>
                      <button type="button" disabled={busy} onClick={() => onSaveRoles(row)}
                        style={{ marginRight: 6, padding: "4px 10px", border: "none", borderRadius: 6, background: C.brand, color: "#fff" }}>
                        Save
                      </button>
                      <button type="button" onClick={() => setEditing(null)}
                        style={{ padding: "4px 10px", border: `1px solid ${C.border}`, borderRadius: 6, background: "#fff" }}>
                        Cancel
                      </button>
                    </>
                  )}
                  {editing !== row.email && (
                    <>
                      {isAdmin && canChangeRoles && row.canEdit && (
                        <button type="button" onClick={() => { setEditing(row.email); setEditRoles(row.roles); }}
                          style={{ marginRight: 6, padding: "4px 10px", border: `1px solid ${C.border}`, borderRadius: 6, background: "#fff" }}>
                          Edit roles
                        </button>
                      )}
                      {canRemove && row.canRemove && (
                        <button type="button" disabled={busy} onClick={() => onRemove(row)}
                          style={{ padding: "4px 10px", border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 6, background: "#fff" }}>
                          Remove
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Administration: Users · Rules & Permissions · Audit log (tabs follow the signed-in user's rules).
export default function Administration() {
  const rules = useRules();
  const showRules = rules.hasRole("ADMIN") || rules.hasRole("MANAGER");
  const showAudit = rules.can("audit.view");
  const showUsers = rules.can("users.view");
  const tabs = [
    showUsers && ["users", "Users"],
    showRules && ["rules", "Rules & Permissions"],
    showAudit && ["audit", "Audit log"],
  ].filter(Boolean);
  const [tab, setTab] = useState("users");
  const current = tabs.find(([id]) => id === tab) ? tab : tabs[0]?.[0];

  if (!rules.loaded) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!tabs.length) return <div style={{ padding: 24 }}>You don&apos;t have access to Administration.</div>;

  return (
    <div>
      {tabs.length > 1 && (
        <div className="admin-tabs" role="tablist">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={current === id}
              className={current === id ? "on" : ""} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>
      )}
      {current === "users" && <UsersTab />}
      {current === "rules" && <RulesPermissions />}
      {current === "audit" && <RulesAuditLog />}
    </div>
  );
}
